import * as bs from 'bootstrap';
import axios from 'smg/axios';
import * as models from 'smg/customer-api/models';
import * as session from 'smg/customer-api/session';
import { UpdateShippingOptionLabel } from 'smg/customer-api/shippingoptions';
import { ShowError } from 'smg/errors';
import * as phone from 'smg/phone';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import * as shippingoptions from '../../shippingoptions';

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const sess = await session.Session();
    const $mainSection = document.querySelector<HTMLElement>('#shipping-options') as HTMLElement;
    const $defaultShippingOptionParent = $mainSection.querySelector<HTMLElement>('#default-shipping-option-parent') as HTMLElement;
    const $defaultShippingOption = $defaultShippingOptionParent.querySelector<HTMLElement>('#default-shipping-option') as HTMLElement;
    const $shippingOptionsListParent = $mainSection.querySelector<HTMLElement>('#shipping-options-list-parent') as HTMLElement;
    const $shippingOptionsList = $shippingOptionsListParent.querySelector<HTMLElement>('#shipping-options-list') as HTMLElement;
    const $addMoreBtn = document.querySelector<HTMLButtonElement>('#add-more') as HTMLButtonElement;

    const $viewMoreBtnParent = template.Clone('#view-more-btn-template').firstElementChild as HTMLElement;
    const $viewMoreBtn = $viewMoreBtnParent?.querySelector('#view-more-btn') as HTMLButtonElement;

    const $currMax = $viewMoreBtnParent?.querySelector('#current-max') as HTMLElement;
    const $totalResults = $viewMoreBtnParent?.querySelector('#total-tab-results') as HTMLElement;

    const userShippingOptions = new Map<string, models.HyejooShippingOption>();

    // Checkout drops options whose stored phone fails the server tel validator; let the customer fix it here.
    const flagInvalidPhone = ($so: HTMLElement, shippingOption: models.HyejooShippingOption) => {
        const $body = $so.querySelector<HTMLElement>('.card-body');
        $body?.querySelector('.invalid-phone-note')?.remove();
        if (!phone.IsValid(shippingOption.address?.phoneNumber?.phone ?? '')) {
            $body?.appendChild(phone.InvalidPhoneNote());
        }
    };

    let pageNumber: number = 0;

    let shippingOptionQueue: models.HyejooShippingOption[] = [];
    let defaultShippingOption: models.HyejooShippingOption | undefined;
    let nextPage = true;

    function setup(): PageLoader<models.HyejooShippingOption> {
        const pl: PageLoader<models.HyejooShippingOption> = new PageLoader<models.HyejooShippingOption>({
            clearResults: false,
            parent: $shippingOptionsList,
            nextBtn: $viewMoreBtn,
            get: () => getNextShippingAddresses().then(shippingOptions => {
                if (shippingOptions.length === 0) {
                    $shippingOptionsListParent.classList.add('d-none');
                    $addMoreBtn.classList.add('text-center');
                } else {
                    $shippingOptionsListParent.classList.remove('d-none');
                    $addMoreBtn.classList.remove('text-center');
                }
                return { rows: shippingOptions, nextPage: shippingOptionQueue.length > 0 };
            }),
            draw: so => {
                if (so.id) {
                    userShippingOptions.set(so.id, so);
                }
                return shippingoptions.Draw(so, {
                    el: template.Clone('#shipping-option-template').firstElementChild as HTMLElement,
                    radio: false,
                    onSetDefault: setDefault,
                }).then($so => {
                    const $edit = $so.querySelector<HTMLButtonElement>('.shipping-option-edit');
                    $edit?.addEventListener('click', editShippingOption);
                    const $delete = $so.querySelector<HTMLButtonElement>('.shipping-option-delete');
                    $delete?.addEventListener('click', promptDeleteShippingOption);
                    flagInvalidPhone($so, so);
                    return $so;
                });
            },
            afterLoad: () => {
                if (shippingOptionQueue.length > 0) {
                    $shippingOptionsList?.appendChild($viewMoreBtnParent);
                } else {
                    $viewMoreBtnParent?.remove();
                }

                $currMax.textContent = String((pl.currentPage() + 1) * 5);
            },
        });
        return pl;
    }

    const pl = setup();

    async function getNextShippingAddresses(): Promise<models.HyejooShippingOption[]> {
        if (shippingOptionQueue.length <= 6 && nextPage) {
            await fetchShippingAddresses();
        }

        return new Promise(resolve => {
            const out: models.HyejooShippingOption[] = [];
            if (shippingOptionQueue.length) {
                for (let i = 0; i < 5; i++) {
                    const so = shippingOptionQueue.shift();
                    if (so) {
                        out.push(so);
                    } else {
                        break;
                    }
                }
            }

            resolve(out);
        });
    }

    async function fetchShippingAddresses(): Promise<models.HyejooShippingOption | undefined> {
        const resp = await axios.get('/shipping-options', {
            params: {
                'sort': 'default,created',
                'max': 10,
                'data': 'id,created,lastOrdered,label,default,address',
                'p': pageNumber || 0,
            },
        });

        const data = models.ShippingOptionsHyejooResponse.fromJSON(resp.data);
        if (!pageNumber) {
            pageNumber = 0;
        }
        shippingOptionQueue.push(...data.shippingOptions);
        nextPage = data.nextPage;
        pageNumber++;
        const first = shippingOptionQueue[0];
        if (first?.default === true) {
            shippingOptionQueue.shift();
            defaultShippingOption = first;
            return first;
        }
        return undefined;
    }

    const getShippingOptionCount = async () => await axios.get('/shipping-options', {
        params: {
            // exclude default option from this count since the pageloader only handles non-default options
            'q': '-label:default',
            'colStats': 'id',
        },
    }).then(resp => models.ColumnStatsResponse.fromJSON(resp.data).count);

    async function reloadShippingOptions() {
        $defaultShippingOption.innerHTML = '';

        userShippingOptions.clear();
        shippingOptionQueue = [];
        defaultShippingOption = undefined;
        QueryString().delete('p');
        pageNumber = 0;
        $shippingOptionsList.innerHTML = '';

        defaultShippingOption = await fetchShippingAddresses();

        if (defaultShippingOption) {
            $defaultShippingOptionParent.classList.remove('d-none');
            userShippingOptions.set(defaultShippingOption.id, defaultShippingOption);
            $defaultShippingOption.innerHTML = '';
            const defaultSo = defaultShippingOption;
            const $so = await shippingoptions.Draw(defaultSo, { el: template.Clone('#shipping-option-template').firstElementChild as HTMLElement, radio: false }).then($so => {
                const $edit = $so.querySelector<HTMLButtonElement>('.shipping-option-edit');
                $edit?.addEventListener('click', editShippingOption);
                const $delete = $so.querySelector<HTMLButtonElement>('.shipping-option-delete');
                $delete?.addEventListener('click', promptDeleteShippingOption);
                flagInvalidPhone($so, defaultSo);
                return $so;
            });
            $defaultShippingOption.appendChild($so);
        } else {
            $defaultShippingOptionParent.classList.add('d-none');
        }

        $totalResults.textContent = await getShippingOptionCount().then(count => String(count));

        pl.load();
    }

    await reloadShippingOptions();

    const setDefault = async (shippingOptionInit: models.IShippingOption) => {
        const so = new models.ShippingOption(shippingOptionInit);
        await UpdateShippingOptionLabel(so.id, 'default').
            then(resp => {
                if (resp) {
                    toast.Success('Default shipping address updated');
                } else {
                    toast.Error('There was an error updating your default shipping address');
                }
            }).
            catch(ShowError).
            finally(() => {
                setTimeout(async () => await reloadShippingOptions());
            });
    };

    $addMoreBtn.addEventListener('click', () => {
        shippingoptions.PromptNew(sess.user).then(() => {
            void reloadShippingOptions();
        }).catch(ShowError);
    });

    function editShippingOption(e: Event) {
        if (e.target instanceof HTMLButtonElement && e.target.classList.contains('shipping-option-edit')) {
            const $so = e.target.closest('.shipping-option') as HTMLElement;
            const id = $so.getAttribute('data-shipping-option-id') || '';
            const so = userShippingOptions.get(id);
            if (so) {
                shippingoptions.PromptEdit(sess.user, so).then(() => {
                    void reloadShippingOptions();
                }).catch(ShowError);
            }
        }
    }

    const $deleteConfirmTemplate = document.querySelector('.delete-confirm-template') as HTMLTemplateElement;
    function promptDeleteShippingOption(e: Event) {
        if (e.target instanceof HTMLButtonElement && e.target.classList.contains('shipping-option-delete')) {
            const $so = e.target.closest('.shipping-option') as HTMLElement;
            const id = $so.getAttribute('data-shipping-option-id') || '';

            const deleteTooltip = new bs.Tooltip(e.target, {
                trigger: 'manual',
                placement: 'top',
                title: 'Are you sure you want to delete this shipping address?',
                html: true,
                offset: [0, 16],
            });

            const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

            const deleteShippingOption = async (id: string) => {
                await axios.delete(`/shipping-options/${id}`).then(() => {
                    toast.Undo('Deleted shipping address.', async () => {
                        await restoreShippingOption(id);
                    });

                    $so.classList.add('d-none');
                    void getShippingOptionCount().then(count => {
                        $totalResults.textContent = String(count);
                        $currMax.textContent = String((pl.currentPage() + 1) * 5);
                    });
                    deleteTooltip?.dispose();
                });
            };

            const restoreShippingOption = async (id: string) => {
                await axios.patch(`/shipping-options/${id}`, {
                    'active': true,
                }).then(() => {
                    toast.Undo('Restored shipping address.', async () => {
                        await deleteShippingOption(id);
                    });

                    $so.classList.remove('d-none');
                    void getShippingOptionCount().then(count => {
                        $totalResults.textContent = String(count);
                        $currMax.textContent = String((pl.currentPage() + 1) * 5);
                    });
                });
            };

            const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
            $confirm.addEventListener('click', async () => {
                await deleteShippingOption(id);
            });

            const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
            $cancel.addEventListener('click', () => {
                deleteTooltip?.dispose();
            });

            deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
            deleteTooltip.show();
        }
    }
}
